import { get as _get } from 'lodash';
import { RootState } from '../app/store/store';

/**
 * Canonical vehicle shape consumed by the Vehicles page. Both claim sources
 * (the `claims` slice and the `claimDetail` slice) are normalized into this.
 */
export interface Vehicle {
  year: string;
  make: string;
  model: string;
  vin: string;
}

const normalizeClaimNumber = (claimNumber?: string) => (claimNumber ?? '').toString().replace(/-/g, '');

// `claims` slice (claims-overview path) stores vehicles under `vehicleDetail` with capitalized keys.
function mapVehicleDetail(vehicle: any): Vehicle {
  return {
    year: _get(vehicle, 'Year', ''),
    make: _get(vehicle, 'Make', ''),
    model: _get(vehicle, 'Model', ''),
    vin: _get(vehicle, 'VIN', ''),
  };
}

// `claimDetail` slice (direct claim-detail path) stores vehicles under `autoClaimDetails` with lowercase keys.
function mapAutoClaimDetail(vehicle: any): Vehicle {
  return {
    year: _get(vehicle, 'year', ''),
    make: _get(vehicle, 'make', ''),
    model: _get(vehicle, 'model', ''),
    vin: _get(vehicle, 'vin', ''),
  };
}

/**
 * Resolves the vehicles for a claim from whichever slice holds it, returning a
 * single canonical shape. Prefers the `claims` slice (overview path); falls back
 * to the `claimDetail` slice only when it holds the same claim (direct claim-detail
 * path), so a stale claimSummary from a previously-viewed claim is never shown.
 */
export function getClaimVehicles(state: RootState, claimNumber: string): Vehicle[] {
  const target = normalizeClaimNumber(claimNumber);

  const openClaims = _get(state, 'claims.openClaims', []) ?? [];
  const closedClaims = _get(state, 'claims.closedClaims', []) ?? [];
  const claimFromList = [...openClaims, ...closedClaims].find(
    (claim: any) => normalizeClaimNumber(claim.claimNumber) === target
  );
  const vehicleDetail = _get(claimFromList, 'vehicleDetail', []);
  if (vehicleDetail.length) {
    return vehicleDetail.map(mapVehicleDetail);
  }

  const claimSummary = _get(state, 'claimDetail.claimSummary');
  if (claimSummary && normalizeClaimNumber(_get(claimSummary, 'claimNumber', '')) === target) {
    return (_get(claimSummary, 'autoClaimDetails', []) ?? []).map(mapAutoClaimDetail);
  }

  return [];
}
